/**
 * Smart local cache service using localStorage.
 *
 * Strategy:
 * 1. Each cacheable collection has a `cache_meta/{collection}` doc in Firestore
 *    with a `lastUpdated` timestamp.
 * 2. On fetch: read only that single meta doc (1 read), compare with cached timestamp.
 * 3. If timestamps match → return localStorage data (0 collection reads).
 * 4. If timestamps differ → fetch full collection, update localStorage + cached timestamp.
 *
 * Security-sensitive collections (user_accounts, admin_users) are never cached.
 */

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

// Collections that must NEVER be cached locally
const EXCLUDED_COLLECTIONS = new Set([
  "user_accounts",
  "admin_users",
  "registrations", // Contains personal data (PII) — admin-only, never cache locally
]);

const META_COLLECTION = "cache_meta";
const CACHE_PREFIX = "fc_"; // firestore cache prefix

// ─── Helpers ────────────────────────────────────────────────────────────────

function cacheKey(collection) {
  return `${CACHE_PREFIX}${collection}`;
}

function metaKey(collection) {
  return `${CACHE_PREFIX}${collection}_ts`;
}

function readLocal(collection) {
  try {
    const raw = localStorage.getItem(cacheKey(collection));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocal(collection, data, serverTs) {
  try {
    localStorage.setItem(cacheKey(collection), JSON.stringify(data));
    localStorage.setItem(metaKey(collection), String(serverTs));
  } catch (e) {
    console.warn("[cache] localStorage write failed:", e.message);
  }
}

function readLocalTs(collection) {
  const ts = localStorage.getItem(metaKey(collection));
  return ts ? Number(ts) : null;
}

function clearLocal(collection) {
  localStorage.removeItem(cacheKey(collection));
  localStorage.removeItem(metaKey(collection));
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Fetch data with cache-first strategy.
 *
 * @param {string} collection - Firestore collection name
 * @param {() => Promise<Array>} fetcher - async function that fetches from Firestore
 * @returns {Promise<Array>} - cached or fresh data
 */
export async function cachedFetch(collection, fetcher) {
  if (EXCLUDED_COLLECTIONS.has(collection)) {
    return fetcher();
  }

  try {
    // 1. Get server's lastUpdated for this collection (1 lightweight read)
    const metaRef = doc(db, META_COLLECTION, collection);
    const metaSnap = await getDoc(metaRef);

    const serverTs = metaSnap.exists()
      ? metaSnap.data().lastUpdated instanceof Timestamp
        ? metaSnap.data().lastUpdated.toMillis()
        : Number(metaSnap.data().lastUpdated)
      : null;

    const localTs = readLocalTs(collection);
    const localData = readLocal(collection);

    // 2. Cache hit: timestamps match and we have local data
    if (serverTs && localTs && serverTs === localTs && localData) {
      console.debug(`[cache] HIT: ${collection}`);
      return localData;
    }

    // 3. Cache miss: fetch from Firestore and update local cache
    console.debug(`[cache] MISS: ${collection} — fetching from server`);
    const freshData = await fetcher();
    writeLocal(collection, freshData, serverTs ?? Date.now());
    return freshData;
  } catch (err) {
    // If cache meta read fails, fall back to direct fetch
    console.warn(
      `[cache] Error for ${collection}, falling back to direct fetch:`,
      err.message,
    );
    return fetcher();
  }
}

/**
 * Call this after any write operation to a collection to invalidate its cache.
 * Updates the cache_meta doc so all clients will refetch on next access.
 *
 * @param {string} collection - Firestore collection name
 */
export async function invalidateCache(collection) {
  if (EXCLUDED_COLLECTIONS.has(collection)) return;

  // Update server-side meta timestamp
  const metaRef = doc(db, META_COLLECTION, collection);
  await setDoc(metaRef, { lastUpdated: serverTimestamp() }, { merge: true });

  // Also clear local cache immediately so current client refetches
  clearLocal(collection);
  console.debug(`[cache] INVALIDATED: ${collection}`);
}

/**
 * Clear all local caches (e.g. on logout).
 */
export function clearAllLocalCaches() {
  const keys = Object.keys(localStorage).filter((k) =>
    k.startsWith(CACHE_PREFIX),
  );
  keys.forEach((k) => localStorage.removeItem(k));
  console.debug(`[cache] Cleared ${keys.length} local cache entries`);
}
